import { DateField, LocalizationProvider, CalendarIcon } from "@mui/x-date-pickers"
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isFuture,
  isValid,
  parse as parseDate,
  parseISO,
  startOfDay
} from "date-fns";
import { Box, Grid, IconButton, Popover, Stack, Tab, Tabs, TextField, Typography, Button } from "@mui/material";
import PropTypes from "prop-types";
import HorizontalRuleRoundedIcon from "@mui/icons-material/HorizontalRuleRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { MIN_DATE } from '../utils/constants';


export function RangePicker(props) {
  const { startDate, setStartDate, endDate, setEndDate, setIsDateRangeValid } = props;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");
  const [savedStartDate, setSavedStartDate] = useState(null);
  const [savedEndDate, setSavedEndDate] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    const onValidate = () => setShowValidation(true);
    const onClear = () => setShowValidation(false);
    window.addEventListener("validate-form", onValidate);
    window.addEventListener("clear-validation", onClear);
    return () => {
      window.removeEventListener("validate-form", onValidate);
      window.removeEventListener("clear-validation", onClear);
    };
  }, []);

  const validateDateRange = ([newStartDate, newEndDate]) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsDateRangeValid(newStartDate && newEndDate && newStartDate <= newEndDate && newStartDate >= MIN_DATE);
  }

  const handleFutureDate = (date) => {
    if (isFuture(date)) {
      return startOfDay(new Date());
    }
    return date;
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDailyRangeChange = (dates) => {
    const [start, end] = dates;
    validateDateRange([start, end]);
  };

  const handleMonthRangeChange = (dates) => {
    const [start, end] = dates;
    let endDt = end && endOfMonth(end);
    endDt = handleFutureDate(endDt);

    validateDateRange([start, endDt]);
  };

  const handleQuarterRangeChange = (dates) => {
    const [start, end] = dates;
    let endDt = end && endOfQuarter(end);
    endDt = handleFutureDate(endDt);

    validateDateRange([start, endDt]);
  };

  const handleYearRangeChange = (dates) => {
    const [start, end] = dates;
    let endDt = end && endOfYear(end);
    endDt = handleFutureDate(endDt);

    validateDateRange([start, endDt]);
  };

  const handleStartDateChange = (date) => {
    const start = parseDate(date, 'yyyy-MM-dd', new Date());
    if (isValid(start)) {
      validateDateRange([handleFutureDate(start), endDate]);
    }
  }

  const handleEndDateChange = (date) => {
    const end = parseDate(date, 'yyyy-MM-dd', new Date());
    if (isValid(end)) {
      validateDateRange([startDate, handleFutureDate(end)]);
    }
  }

  // Format input to yyyy-MM-dd format
  const formatDateInput = (value, previousValue) => {
    // If the new value is shorter than previous, user is backspacing - allow free deletion
    if (previousValue && value.length < previousValue.length) {
      // Remove all non-numeric characters except dashes
      let cleaned = value.replace(/[^\d-]/g, '');
      
      // If completely empty, allow it
      if (cleaned.length === 0) {
        return '';
      }
      
      // When backspacing, allow partial formats (e.g., "2024-1" or "2024-12-")
      // Just ensure it follows the pattern but don't force completion
      return cleaned;
    }
    
    // Normal typing - format to yyyy-MM-dd
    // Remove all non-numeric characters except dashes
    let cleaned = value.replace(/[^\d-]/g, '');
    
    // Remove dashes that aren't in the right positions
    let parts = cleaned.split('-');
    let formatted = '';
    
    if (parts.length > 0 && parts[0]) {
      formatted += parts[0].substring(0, 4); // Year (max 4 digits)
      if (parts[0].length >= 4 || (parts.length > 1 && parts[1])) {
        formatted += '-';
        if (parts.length > 1 && parts[1]) {
          formatted += parts[1].substring(0, 2); // Month (max 2 digits)
          if (parts[1].length >= 2 || (parts.length > 2 && parts[2])) {
            formatted += '-';
            if (parts.length > 2 && parts[2]) {
              formatted += parts[2].substring(0, 2); // Day (max 2 digits)
            }
          }
        }
      }
    }
    
    return formatted;
  };

  // Sync input values when dates change externally (e.g., from calendar)
  useEffect(() => {
    if (startDate) {
      setStartDateInput(format(startDate, "yyyy-MM-dd"));
    } else {
      setStartDateInput("");
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setEndDateInput(format(endDate, "yyyy-MM-dd"));
    } else {
      setEndDateInput("");
    }
  }, [endDate]);

  function CustomTabPanel(tabPanelProps) {
    const { children, value: tabValueProp, index, ...other } = tabPanelProps;

    return (
      <div
        role="tabpanel"
        hidden={tabValueProp !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {tabValueProp === index && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ p: 1 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  };

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const datePickerCommonProps = {
    disabledKeyboardNavigation: true,
    dateFormat: "yyyy-MM-dd",
    selectsRange: true,
    showTimeSelect: false,
    inline: true,
    minDate: MIN_DATE,
    maxDate: new Date(),
    parseInputDates: parseISO,
    monthsShown: 1,
    fixedHeight: true,
    startDate: startDate,
    endDate: endDate
  }

  const handleOk = () => {
    setSavedStartDate(startDate);
    setSavedEndDate(endDate);
    setOpen(false);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    // Restore saved dates if they exist, otherwise clear
    if (savedStartDate !== null && savedEndDate !== null) {
      setStartDate(savedStartDate);
      setEndDate(savedEndDate);
      validateDateRange([savedStartDate, savedEndDate]);
    }
    setOpen(false);
    setAnchorEl(null);
  };

  // Save current dates when calendar opens (only when open changes from false to true)
  useEffect(() => {
    if (open) {
      setSavedStartDate(startDate);
      setSavedEndDate(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const dateFieldCommonProps = {
    size: "small",
    style: { width: 120 },
    format: "yyyy-MM-dd",
    clearable: true,
    required: true
  }


  return (
    <div data-testid='range-picker'>
      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ display: 'flex', alignItems: 'flex-end' }}>
        {/* Start Date */}
        <Box>
          <TextField
            size="small"
            label="Start Date"
            value={startDateInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Format the input to yyyy-MM-dd, passing previous value for backspace detection
              const formatted = formatDateInput(inputValue, startDateInput);
              setStartDateInput(formatted);
              
              // Try to parse and validate the date if it's complete (10 characters: yyyy-MM-dd)
              if (formatted.length === 10) {
                const parsed = parseDate(formatted, 'yyyy-MM-dd', new Date());
                if (isValid(parsed)) {
                  handleStartDateChange(format(parsed, "yyyy-MM-dd"));
                }
              } else if (formatted.length === 0) {
                setStartDate(null);
                setIsDateRangeValid(false);
              }
            }}
            onBlur={(e) => {
              const inputValue = e.target.value;
              if (inputValue && inputValue.length === 10) {
                const parsed = parseDate(inputValue, 'yyyy-MM-dd', new Date());
                if (isValid(parsed)) {
                  handleStartDateChange(format(parsed, "yyyy-MM-dd"));
                  setStartDateInput(format(parsed, "yyyy-MM-dd"));
                } else {
                  // Reset to last valid date or empty
                  setStartDateInput(startDate ? format(startDate, "yyyy-MM-dd") : "");
                }
              } else if (inputValue.length > 0 && inputValue.length < 10) {
                // Incomplete date, reset to last valid or empty
                setStartDateInput(startDate ? format(startDate, "yyyy-MM-dd") : "");
              } else {
                setStartDateInput("");
              }
            }}
            placeholder={(showValidation && !startDate) ? "Required" : "YYYY-MM-DD"}
            error={(showValidation && !startDate) || (startDate && endDate && startDate > endDate) || (startDate && startDate < MIN_DATE) || (startDate && !isValid(startDate))}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpen(true);
                    setAnchorEl(event.currentTarget);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <ArrowDropDownIcon sx={{ color: (showValidation && !startDate) ? "#d32f2f" : "#000270" }} />
                </IconButton>
              ),
            }}
            sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "& fieldset": {
                  borderColor: (showValidation && !startDate) ? "#d32f2f" : "#000270",
                },
                "&:hover fieldset": {
                  borderColor: (showValidation && !startDate) ? "#d32f2f" : "#000270",
                },
                "&.Mui-focused fieldset": {
                  borderColor: (showValidation && !startDate) ? "#d32f2f" : "#000270",
                },
              },
              "& .MuiInputLabel-root": {
                color: (showValidation && !startDate) ? "#d32f2f" : "#000270",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: (showValidation && !startDate) ? "#d32f2f" : "#000270",
              },
            }}
          />
        </Box>

        {/* Dash Separator */}
        <Box sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <HorizontalRuleRoundedIcon sx={{ color: "#000" }} />
        </Box>

        {/* End Date */}
        <Box>
          <TextField
            size="small"
            label="End Date"
            value={endDateInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Format the input to yyyy-MM-dd, passing previous value for backspace detection
              const formatted = formatDateInput(inputValue, endDateInput);
              setEndDateInput(formatted);
              
              // Try to parse and validate the date if it's complete (10 characters: yyyy-MM-dd)
              if (formatted.length === 10) {
                const parsed = parseDate(formatted, 'yyyy-MM-dd', new Date());
                if (isValid(parsed)) {
                  handleEndDateChange(format(parsed, "yyyy-MM-dd"));
                }
              } else if (formatted.length === 0) {
                setEndDate(null);
                setIsDateRangeValid(false);
              }
            }}
            onBlur={(e) => {
              const inputValue = e.target.value;
              if (inputValue && inputValue.length === 10) {
                const parsed = parseDate(inputValue, 'yyyy-MM-dd', new Date());
                if (isValid(parsed)) {
                  handleEndDateChange(format(parsed, "yyyy-MM-dd"));
                  setEndDateInput(format(parsed, "yyyy-MM-dd"));
                } else {
                  // Reset to last valid date or empty
                  setEndDateInput(endDate ? format(endDate, "yyyy-MM-dd") : "");
                }
              } else if (inputValue.length > 0 && inputValue.length < 10) {
                // Incomplete date, reset to last valid or empty
                setEndDateInput(endDate ? format(endDate, "yyyy-MM-dd") : "");
              } else {
                setEndDateInput("");
              }
            }}
            placeholder={(showValidation && !endDate) ? "Required" : "YYYY-MM-DD"}
            error={(showValidation && !endDate) || (startDate && endDate && startDate > endDate) || (endDate && !isValid(endDate))}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpen(true);
                    setAnchorEl(event.currentTarget);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <ArrowDropDownIcon sx={{ color: (showValidation && !endDate) ? "#d32f2f" : "#000270" }} />
                </IconButton>
              ),
            }}
            sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "& fieldset": {
                  borderColor: (showValidation && !endDate) ? "#d32f2f" : "#000270",
                },
                "&:hover fieldset": {
                  borderColor: (showValidation && !endDate) ? "#d32f2f" : "#000270",
                },
                "&.Mui-focused fieldset": {
                  borderColor: (showValidation && !endDate) ? "#d32f2f" : "#000270",
                },
              },
              "& .MuiInputLabel-root": {
                color: (showValidation && !endDate) ? "#d32f2f" : "#000270",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: (showValidation && !endDate) ? "#d32f2f" : "#000270",
              },
            }}
          />
        </Box>
      </Stack>

      <Popover 
        open={open}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setOpen(false) }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: "16px",
          }
        }}
      >
        <Box sx={{ width: "100%", p: 2 }}>
          {/* Header with Calendar title and Tabs */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ color: "#000270", fontWeight: 600 }}>
              Calendar
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Date Range picker"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  color: "#666",
                  minWidth: "auto",
                  px: 2,
                },
                "& .Mui-selected": {
                  color: "#000270",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#000270",
                },
              }}
            >
              <Tab label="Daily" {...a11yProps(0)} />
              <Tab label="Monthly" {...a11yProps(1)} />
              <Tab label="Quarterly" {...a11yProps(2)} />
              <Tab label="Yearly" {...a11yProps(3)} />
            </Tabs>
          </Box>

          {/* Calendar Content */}
          <CustomTabPanel value={tabValue} index={0}>
            <DatePicker
              {...datePickerCommonProps}
              onChange={handleDailyRangeChange}
              monthsShown={2}
            />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            <DatePicker
              {...datePickerCommonProps}
              onChange={handleMonthRangeChange}
              showMonthYearPicker
            />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={2}>
            <DatePicker
              {...datePickerCommonProps}
              onChange={handleQuarterRangeChange}
              showQuarterYearPicker
            />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={3}>
            <DatePicker
              {...datePickerCommonProps}
              onChange={handleYearRangeChange}
              showYearPicker
            />
          </CustomTabPanel>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              onClick={handleCancel}
              sx={{
                color: "#000270",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              CANCEL
            </Button>
            <Button
              onClick={handleOk}
              sx={{
                color: "#000270",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
}
